/**
 * useCompanionOnboardingForm.ts
 * Custom hook for managing Companion onboarding form state and validation using react-hook-form.
 *
 * Responsibilities:
 * - Manage form data for all 5 onboarding steps with react-hook-form
 * - Validate each step before allowing progression
 * - Track current screen state (intro, steps 1-5, success) for UI navigation
 * - Provide navigation handlers (next, back, skip)
 * - Use GraphQL schema types for form data structure
 *
 * Architecture decisions:
 * - Uses react-hook-form for efficient form state management and validation
 * - CompanionOnboardingScreen is a frontend-only enum for UI state (not sent to backend)
 * - CompanionOnboardingFormData uses CompanionOnboardingPayloadInput from GraphQL schema
 * - Form data types are generated from backend schema for consistency
 *
 * Validation rules:
 * - Step 1: At least 1 interest selected
 * - Step 2: At least 1 career path selected
 * - Step 3: At least 1 skill AND at least 1 method selected
 * - Step 4: At least 1 motivation selected AND goal selected
 * - Step 5: All required fields filled (phone if SMS enabled)
 */
import { useState, useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { CompanionOnboardingPayloadInput } from "../graphql/generated/graphql";

/**
 * CompanionOnboardingScreen enum
 * Frontend-only enum for managing UI state in the onboarding flow.
 * This is not sent to the backend - it only controls which screen is displayed.
 */
export enum CompanionOnboardingScreen {
  Intro = "INTRO",
  Step_1 = "STEP_1",
  Step_2 = "STEP_2",
  Step_3 = "STEP_3",
  Step_4 = "STEP_4",
  Step_5 = "STEP_5",
  Success = "SUCCESS",
}

// Form data type from GraphQL schema
export type CompanionOnboardingFormData = CompanionOnboardingPayloadInput;

export interface UseCompanionOnboardingFormReturn {
  currentScreen: CompanionOnboardingScreen;
  formData: CompanionOnboardingFormData;
  canProceed: boolean; // Whether user can proceed to next step (validation result)
  setValue: (field: keyof CompanionOnboardingFormData, value: any) => void; // Update form field
  getValues: () => CompanionOnboardingFormData; // Get all form values
  handleStart: () => void; // Move from intro to first step
  handleNext: () => void; // Move to next step (with validation)
  handleBack: () => void; // Move to previous step
  handleSkip: () => void;
  resetForm: () => void;
  goToSuccess: () => void;
  setPhoneVerified: (verified: boolean) => void; // Update phone verification status
  setSmsOptIn: (optIn: boolean) => void; // Update SMS opt-in status
}

const initialFormData: CompanionOnboardingFormData = {
  interests: [],
  careerInterests: [],
  skillsToImprove: [],
  learningStyles: [],
  motivations: [],
  semesterGoal: "",
  assignmentReminders: true,
  studyTimePreference: null,
  checkInFrequency: null,
  textReminders: false,
};

const validateScreen = (
  screen: CompanionOnboardingScreen,
  formData: CompanionOnboardingFormData,
  phoneVerified: boolean,
  smsOptIn: boolean
): boolean => {
  // Step 5: If text reminders enabled, require phone verification and SMS opt-in
  if (screen === CompanionOnboardingScreen.Step_5) {
    if (formData.textReminders) {
      return phoneVerified && smsOptIn;
    }
  }
  // Allow progression through all other steps regardless of selections
  return true;
};

export function useCompanionOnboardingForm(): UseCompanionOnboardingFormReturn {
  const [currentScreen, setCurrentScreen] = useState<CompanionOnboardingScreen>(CompanionOnboardingScreen.Intro);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [smsOptIn, setSmsOptIn] = useState(false);
  
  const { watch, setValue, reset, getValues } = useForm<CompanionOnboardingFormData>({
    defaultValues: initialFormData,
    mode: "onChange",
  });

  const formData = watch();

  const handleStart = useCallback(() => {
    // Transition from intro screen to the first step
    setCurrentScreen(CompanionOnboardingScreen.Step_1);
  }, []);

  const handleNext = useCallback(() => {
    // Move to next screen if validation passes for current screen
    const screenOrder = [
      CompanionOnboardingScreen.Step_1,
      CompanionOnboardingScreen.Step_2,
      CompanionOnboardingScreen.Step_3,
      CompanionOnboardingScreen.Step_4,
      CompanionOnboardingScreen.Step_5,
    ];
    
    const currentIndex = screenOrder.indexOf(currentScreen);
    if (currentIndex !== -1 && validateScreen(currentScreen, formData, phoneVerified, smsOptIn) && currentIndex < screenOrder.length - 1) {
      setCurrentScreen(screenOrder[currentIndex + 1]);
    }
  }, [currentScreen, formData, phoneVerified, smsOptIn]);

  const handleBack = useCallback(() => {
    // Move back one screen without validation (allow undoing entries)
    const screenOrder = [
      CompanionOnboardingScreen.Intro,
      CompanionOnboardingScreen.Step_1,
      CompanionOnboardingScreen.Step_2,
      CompanionOnboardingScreen.Step_3,
      CompanionOnboardingScreen.Step_4,
      CompanionOnboardingScreen.Step_5,
    ];
    
    const currentIndex = screenOrder.indexOf(currentScreen);
    if (currentIndex > 0) {
      setCurrentScreen(screenOrder[currentIndex - 1]);
    }
  }, [currentScreen]);

  const handleSkip = useCallback(() => {
    // Skip onboarding entirely - user dismisses the intro without starting
    setCurrentScreen(CompanionOnboardingScreen.Success);
  }, []);

  const goToSuccess = useCallback(() => {
    // Triggered when all steps complete - show success screen and prepare for form submission
    setCurrentScreen(CompanionOnboardingScreen.Success);
  }, []);

  const resetForm = useCallback(() => {
    // Clear all form data and return to intro screen (used when modal closes/reopens)
    setCurrentScreen(CompanionOnboardingScreen.Intro);
    reset(initialFormData);
    setPhoneVerified(false);
    setSmsOptIn(false);
  }, [reset]);

  // Recalculate canProceed whenever screen or form data changes
  // This determines if the "Next" button should be enabled
  const canProceed = useMemo(
    () => validateScreen(currentScreen, formData, phoneVerified, smsOptIn),
    [currentScreen, formData, phoneVerified, smsOptIn]
  );

  return {
    currentScreen,
    formData,
    canProceed,
    setValue,
    getValues,
    handleStart,
    handleNext,
    handleBack,
    handleSkip,
    resetForm,
    goToSuccess,
    setPhoneVerified,
    setSmsOptIn,
  };
}
